import React, { useMemo, useRef, useState } from 'react';
import classes from '../styles.module.scss';
import formClasses from '../../styles.module.scss';
import { createCn } from '../../../helpers/utils';
import { AiTestResult } from '../../../services/ai-settings.service';
import { AiProviderPreset, resolveActivePresetId } from '../utils/ai-provider-presets';

const cn = createCn('ai-settings', classes);

interface AiSettingsGroupProps {
  title: string;
  description: string;
  baseUrl: string;
  onBaseUrlChange: (value: string) => void;
  apiKeySet: boolean;
  apiKeyValue: string;
  onApiKeyChange: (value: string) => void;
  model: string;
  onModelChange: (value: string) => void;
  /** Пресеты провайдера над полем Base URL (LLM — полный список, эмбеддинги/VLM — «Локальный» + «Свой URL»). */
  presets?: AiProviderPreset[];
  /** Сброс живого списка моделей при смене пресета (в т.ч. когда base_url не меняется — «Свой URL»). */
  onPresetChange?: () => void;
  onTest?: () => void;
  testing?: boolean;
  testResult?: AiTestResult | null;
  /** Живой список моделей провайдера (задача M1): подставляется в даталист поля «Модель». */
  models?: string[];
  modelsLoading?: boolean;
  modelsError?: string;
  onFetchModels?: () => void;
}

const AiSettingsGroup = ({
  title,
  description,
  baseUrl,
  onBaseUrlChange,
  apiKeySet,
  apiKeyValue,
  onApiKeyChange,
  model,
  onModelChange,
  presets,
  onPresetChange,
  onTest,
  testing,
  testResult,
  models,
  modelsLoading,
  modelsError,
  onFetchModels,
}: AiSettingsGroupProps) => {
  const hasLiveModels = !!models && models.length > 0;
  const [modelsOpen, setModelsOpen] = useState(false);
  const modelBlurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Фильтруем список по уже введённому тексту; пустой ввод или точное совпадение — показываем всё.
  const filteredModels = useMemo(() => {
    if (!models) return [];
    const q = model.trim().toLowerCase();
    if (!q || models.some((m) => m.toLowerCase() === q)) return models;
    return models.filter((m) => m.toLowerCase().includes(q));
  }, [models, model]);

  const openModels = () => {
    if (modelBlurTimer.current) clearTimeout(modelBlurTimer.current);
    if (hasLiveModels) setModelsOpen(true);
  };
  const closeModelsSoon = () => {
    modelBlurTimer.current = setTimeout(() => setModelsOpen(false), 120);
  };
  const pickModel = (value: string) => {
    onModelChange(value);
    setModelsOpen(false);
  };

  const activePresetId = useMemo(
    () => (presets ? resolveActivePresetId(presets, baseUrl) : undefined),
    [presets, baseUrl],
  );
  const activePreset = presets?.find((preset) => preset.id === activePresetId);

  const handlePresetChange = (id: string) => {
    const preset = presets?.find((p) => p.id === id);
    onPresetChange?.();
    if (preset?.applyBaseUrl) {
      onBaseUrlChange(preset.baseUrl);
    }
  };

  return (
    <div className={cn('group')}>
      <div className={cn('group-title')}>{title}</div>
      <div className={cn('group-desc')}>{description}</div>

      {presets && (
        <div className={formClasses['form-group']}>
          <label>Провайдер</label>
          <select value={activePresetId} onChange={(e) => handlePresetChange(e.target.value)}>
            {presets.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label}
              </option>
            ))}
          </select>
          {activePreset?.hint && <div className={cn('provider-hint')}>{activePreset.hint}</div>}
        </div>
      )}

      <div className={formClasses['form-group']}>
        <label>Base URL</label>
        <input
          value={baseUrl}
          onChange={(e) => onBaseUrlChange(e.target.value)}
          placeholder={activePreset?.baseUrlPlaceholder || 'https://api.example.com/v1'}
          autoComplete="off"
        />
      </div>

      <div className={formClasses['form-group']}>
        <label>
          API-ключ{' '}
          <span className={cn('key-badge', { set: apiKeySet })}>
            {apiKeySet ? 'ключ задан' : 'ключ не задан'}
          </span>
        </label>
        <input
          type="password"
          value={apiKeyValue}
          onChange={(e) => onApiKeyChange(e.target.value)}
          placeholder="Оставьте пустым, чтобы не менять"
          autoComplete="new-password"
        />
      </div>

      <div className={formClasses['form-group']}>
        <label>Модель</label>
        <div className={cn('model-input-row')}>
          <div className={cn('model-combo')}>
            <input
              value={model}
              onChange={(e) => { onModelChange(e.target.value); openModels(); }}
              onFocus={openModels}
              onBlur={closeModelsSoon}
              placeholder="например, gpt-4o-mini"
              autoComplete="off"
            />
            {hasLiveModels && (
              <button
                type="button"
                className={cn('model-combo-toggle', { open: modelsOpen })}
                aria-label="Показать список моделей"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => (modelsOpen ? setModelsOpen(false) : openModels())}
              >
                ▾
              </button>
            )}
            {modelsOpen && hasLiveModels && (
              <ul className={cn('model-combo-list')}>
                {filteredModels.length === 0 && (
                  <li className={cn('model-combo-empty')}>Нет совпадений — можно ввести своё имя</li>
                )}
                {filteredModels.map((m) => (
                  <li key={m}>
                    <button
                      type="button"
                      className={cn('model-combo-option', { active: m === model })}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickModel(m)}
                    >
                      {m}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {onFetchModels && (
            <button
              type="button"
              className={cn('models-fetch-btn')}
              onClick={onFetchModels}
              disabled={!baseUrl.trim() || modelsLoading}
            >
              {modelsLoading ? 'Загрузка…' : 'Загрузить модели'}
            </button>
          )}
        </div>

        {hasLiveModels && (
          <div className={cn('models-hint')}>
            Доступно моделей: {models!.length} — нажмите на поле и выберите из списка или введите своё имя.
          </div>
        )}

        {modelsError && <div className={cn('models-error')}>{modelsError}</div>}

        {!hasLiveModels && activePreset && activePreset.modelHints.length > 0 && (
          <div className={cn('model-chips')}>
            {activePreset.modelHints.map((hint) => (
              <button
                key={hint}
                type="button"
                className={cn('model-chip')}
                onClick={() => onModelChange(hint)}
              >
                {hint}
              </button>
            ))}
          </div>
        )}
      </div>

      {onTest && (
        <div className={cn('group-test')}>
          <button type="button" className={cn('test-btn')} onClick={onTest} disabled={testing}>
            {testing ? 'Проверка…' : 'Проверить подключение'}
          </button>
          {testResult && (
            <div className={cn('test-result', { ok: testResult.ok, fail: !testResult.ok })}>
              {testResult.ok
                ? `Работает${testResult.latency_ms != null ? `, ${testResult.latency_ms}мс` : ''}`
                : testResult.error || 'Ошибка подключения'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AiSettingsGroup;
